import { Grid, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Controller, useFormContext } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

interface FileUploadTextfield {
  name: string;
  label: string;
  onChange?: any;
  validate?: any[];
  errorMessage?: any;
  fileSizeTooptip?: boolean;
  disabled?: boolean;
  actionButton?: boolean;
}

interface ComponentCell {
  name: string;
  label: string;
  value: any;
  errors: any;
  disabled?: boolean;
  validate?: any[];
  errorMessage?: any;
  fileSizeTooptip?: boolean;
  onChange?: any;
  actionButton?: boolean;
}

const ComponentCell = ({ name, label, value, errors, validate, onChange, errorMessage, fileSizeTooptip, disabled, actionButton }: ComponentCell) => {
  const { setError, clearErrors } = useFormContext();
  const [extValidate, setExtValidate] = useState<any>();
  const [fileData, setFileData] = useState({
    file: '',
    fileName: value ?? '',
    gridSize: value ? 11 : 12
  });

  useEffect(() => {
    setFileData((prevState: any) => ({
      ...prevState,
      file: '',
      fileName: value ?? '',
      gridSize: value ? 11 : 12
    }));
  }, [value]);

  useEffect(() => {
    if (validate) {
      setExtValidate(validate);
    }
  }, [validate])

  const validateExt = (ext: string) => {
    if (!extValidate) return true
    if (extValidate?.includes(ext.toUpperCase())) {
      clearErrors(name);
      return true;
    }

    setError(name, {
      type: "custom",
      message: errorMessage ?? "Please upload correct format file."
    });
    return false;
  };

  const handleDrop = useCallback((acceptedFiles: any) => {
    const file = acceptedFiles[0];
    if (!file) return false;
    if (!validateExt(file.name.split('.').pop())) return false;

    const isGreaterThan50MB = file?.size > 50 * 1024 * 1024;

    if (fileSizeTooptip && isGreaterThan50MB) {
      setError(name, {
        type: "custom",
        message: "File size must not exceed 50MB."
      });
      return false;
    }
    else clearErrors(name);


    setFileData((prevState: any) => ({
      ...prevState,
      file: file,
      fileName: file.name
    }));
    onChange(file);
  }, [extValidate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
  });

  const handleChange = () => {
    onChange(null);
  }

  return (
    <>
      <Grid container spacing={1}>
        <Grid item lg={fileData?.gridSize} sm={fileData?.gridSize} xl={fileData?.gridSize} xs={fileData?.gridSize}>
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            {fileSizeTooptip ?
              <Tooltip
                title={
                  <Typography style={{ padding: 5 }} variant='body2'>{`Max size is 50 MB`}</Typography>
                }
                arrow>
                <TextField
                  size="small"
                  fullWidth
                  id="outlined-basic"
                  label={fileData?.fileName ? "" : label}
                  placeholder={label}
                  variant="outlined"
                  disabled={disabled}
                  value={fileData?.fileName} />
              </Tooltip>
              :
              <TextField
                size="small"
                fullWidth
                id="outlined-basic"
                label={fileData?.fileName ? "" : label}
                placeholder={label}
                variant="outlined"
                disabled={disabled}
                value={fileData?.fileName} />}

          </div>
        </Grid>
        {actionButton && (
          fileData?.fileName && (
            <Grid item lg={1} sm={1} xl={1} xs={1}>
              <IconButton
                aria-label="close"
                onClick={handleChange}
              >
                <Tooltip title={<Typography variant="body1">Remove File</Typography>}>
                  <CloseIcon />
                </Tooltip>
              </IconButton>
            </Grid>
          )
        )}
      </Grid >
      {
        errors[name] && (
          <Grid container spacing={0}>
            <Grid item lg={11} sm={11} xl={11} xs={11}><label className="errorMsg">{errors[name]?.message?.toString()}</label></Grid>
            <Grid item lg={1} sm={1} xl={1} xs={1}>
              <Grid
                container
                direction="row"
                sx={{
                  justifyContent: "flex-end",
                  alignItems: "flex-start",
                }}
              >
                <Tooltip
                  title={
                    <Typography style={{ padding: 5 }} variant='body2'>{`Please upload these extension file: ${extValidate?.join(', ')}`}</Typography>
                  }
                  arrow>
                  <HelpOutlineIcon style={{ color: '#616a6b' }} />
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        )
      }
    </>
  );
};

export const FileUploadTextfield = ({ name, label, onChange, validate, errorMessage, fileSizeTooptip, disabled, actionButton }: FileUploadTextfield) => {
  const { control, formState: { errors }, } = useFormContext();
  const handleChange = (val: any) => {
    onChange(val);
  };
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <ComponentCell
          name={name}
          label={label}
          value={field.value}
          validate={validate}
          disabled={disabled}
          errorMessage={errorMessage}
          fileSizeTooptip={fileSizeTooptip}
          errors={errors}
          actionButton={actionButton}
          onChange={(val: File) => {
            field.onChange(val?.name);
            handleChange(val);
          }}
        />
      )}
    />
  );
};
